import { Pipe, PipeTransform } from '@angular/core';

export interface ScaledExchangeRate {
  /** Left hand side of the quote, always a power of ten. */
  unit: number;
  /** Right hand side, i.e. unit * rate. */
  value: number;
}

/**
 * Scales an exchange rate up to a readable unit so it can still be rendered
 * with two decimals.
 *
 * Rates are directional. XOF/EUR is priced separately from EUR/XOF, so the
 * quote cannot be inverted for display without showing a rate that is not the
 * one being applied. Instead the left hand side is multiplied by ten until the
 * right hand side reaches 1.
 *
 *   0.001538 -> { unit: 1000, value: 1.538 }   "1,000 XOF = 1.54 EUR"
 *   650      -> { unit: 1,    value: 650 }     "1 EUR = 650.00 XOF"
 */
@Pipe({
  name: 'exchangeRateScale',
  standalone: true,
})
export class ExchangeRateScalePipe implements PipeTransform {
  private static readonly MAX_UNIT = 1_000_000;

  transform(rate: number | string | null | undefined): ScaledExchangeRate {
    const parsed = Number(rate);

    if (rate == null || rate === '' || !Number.isFinite(parsed) || parsed <= 0) {
      return { unit: 1, value: 0 };
    }

    let unit = 1;
    while (parsed * unit < 1 && unit < ExchangeRateScalePipe.MAX_UNIT) {
      unit *= 10;
    }

    return { unit, value: parsed * unit };
  }
}
